import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { tierUnitPrice, gbp, estimateShipping, zoneCountries } from "@/lib/pricing";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { RestockNotify } from "@/components/RestockNotify";

const visibleProductSizes = (allSizes, wholesale) => allSizes.filter((size) => {
  const ml = size.ml || 0;
  return ml < 20000 && (wholesale || ml < 10000);
});

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const { user } = useAuth();
  const wholesale = !!(user && ["wholesale", "admin"].includes(user.role));
  const [flavor, setFlavor] = useState(null);
  const [sizes, setSizes] = useState([]);
  const [bases, setBases] = useState([]);
  const [zones, setZones] = useState([]);
  const [shipCountry, setShipCountry] = useState("United Kingdom");
  const [baseLabel, setBaseLabel] = useState("Base");
  const [base, setBase] = useState("CO");
  const [sizeId, setSizeId] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    Promise.all([api.get("/flavors"), api.get("/sizes"), api.get("/bases"), api.get("/content"), api.get("/shipping/zones")]).then(([f, s, b, c, z]) => {
      const visibleSizes = visibleProductSizes(s.data, wholesale);
      setFlavor(f.data.find((x) => x.id === id));
      setSizes(visibleSizes);
      setBases(b.data);
      if (b.data[0]) setBase(b.data[0].code);
      setBaseLabel(c.data?.base_label || "Base");
      setZones(z.data);
      if (visibleSizes[0]) setSizeId(visibleSizes[0].id);
    });
  }, [id, wholesale]);

  const size = useMemo(() => sizes.find((s) => s.id === sizeId), [sizes, sizeId]);
  const tiers = size ? (wholesale && size.wholesale_tiers ? size.wholesale_tiers : size.tiers) : [];
  const unit = size ? tierUnitPrice(tiers, qty) : 0;
  const outOfStock = flavor ? flavor.stock <= 0 : false;
  const activeBase = useMemo(() => bases.find((b) => b.code === base), [bases, base]);
  const countries = useMemo(() => zoneCountries(zones), [zones]);
  const ship = useMemo(
    () => (size ? estimateShipping(zones, shipCountry, size.weight_g * qty, unit * qty) : null),
    [zones, shipCountry, size, qty, unit]
  );

  if (!flavor || !size) return <div className="flex h-[50vh] items-center justify-center mono text-xs uppercase tracking-widest text-muted-foreground">Loading…</div>;
  // Guest flavours are ordered only from the Guest Collection flow, never as regular singles.
  if (flavor.range_type === "guest") return <Navigate to="/guest-collection" replace />;

  const addToCart = () => {
    if (outOfStock) { toast.error(`${flavor.name} is out of stock`); return; }
    if (qty > flavor.stock) { toast.error(`Only ${flavor.stock} in stock`); return; }
    addItem({
      key: `single-${flavor.id}-${base}-${size.id}${wholesale ? "-w" : ""}`,
      type: "single",
      flavor_id: flavor.id, flavor_name: flavor.name, accent_color: flavor.accent_color,
      base, size_id: size.id, size_label: size.label,
      quantity: qty, unit_price: unit, weight_g: size.weight_g, is_wholesale: wholesale,
    });
    toast.success(`Added ${qty} × ${flavor.name} ${size.label} (${base})`);
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-10">
      <Link to="/shop" className="mono inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground"><ArrowLeft className="h-3.5 w-3.5" /> Back to shop</Link>
      <div className="mt-6 grid grid-cols-1 gap-px border border-border bg-border lg:grid-cols-2">
        {/* Visual */}
        <div className="flex min-h-[380px] items-center justify-center bg-card p-12">
          {flavor.image_url ? (
            <img src={flavor.image_url} alt={flavor.name} data-testid="product-image" className="max-h-[340px] w-auto object-contain" style={{ opacity: outOfStock ? 0.4 : 1 }} />
          ) : (
            <div className="h-56 w-56 rounded-full shadow-inner" style={{ background: flavor.accent_color }} />
          )}
        </div>
        {/* Config */}
        <div className="bg-background p-8 lg:p-12">
          <h1 className="font-heading text-4xl font-bold tracking-tight">{flavor.name}</h1>
          <p className="mt-3 text-muted-foreground">{flavor.description}</p>
          <div className="mt-4" data-testid="product-stock-status">
            {outOfStock ? (
              <span className="mono border border-destructive px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-destructive">Out of Stock</span>
            ) : flavor.stock <= (flavor.low_stock_threshold ?? 10) ? (
              <span className="mono border border-border px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Low stock · {flavor.stock} left</span>
            ) : (
              <span className="mono border border-border px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-green-600">In Stock</span>
            )}
          </div>

          {/* Base */}
          <div className="mt-8">
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{baseLabel}</div>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="base-selector">
              {bases.map((b) => (
                <button key={b.code} data-testid={`base-${b.code}`} onClick={() => setBase(b.code)}
                  className={`border px-6 py-2.5 text-sm font-medium transition-colors ${base === b.code ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground"}`}>
                  {b.name || b.code}
                </button>
              ))}
            </div>
            {activeBase?.description ? (
              <p className="mt-3 text-sm text-muted-foreground" data-testid="base-description">{activeBase.description}</p>
            ) : null}
          </div>

          {/* Size */}
          <div className="mt-6">
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Size</div>
            <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5" data-testid="size-selector">
              {sizes.map((s) => (
                <button key={s.id} data-testid={`size-${s.label}`} onClick={() => setSizeId(s.id)}
                  className={`border px-2 py-2 text-xs font-medium transition-colors ${sizeId === s.id ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground"}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Quantity */}
          {!outOfStock && (<div>
          <div className="mt-6">
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Quantity</div>
            <div className="mt-3 flex items-center gap-3">
              <div className="flex items-center border border-border">
                <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:bg-muted"><Minus className="h-4 w-4" /></button>
                <input data-testid="qty-input" type="number" min="1" value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="w-16 border-x border-border bg-transparent py-2 text-center mono" />
                <button data-testid="qty-plus" onClick={() => setQty((q) => q + 1)} className="p-3 hover:bg-muted"><Plus className="h-4 w-4" /></button>
              </div>
              <div className="text-sm text-muted-foreground">Unit weight <span className="mono">{size.weight_g}g</span></div>
            </div>
          </div>

          {/* Tier table */}
          <div className="mt-6 border border-border">
            <div className="mono flex items-center justify-between border-b border-border bg-muted px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <span>{wholesale ? "Wholesale Pricing" : "Tiered Pricing"} · per bottle</span>
              {wholesale && <span className="border border-foreground px-2 py-0.5 text-foreground" data-testid="wholesale-badge">Wholesale rates</span>}
            </div>
            <div className="divide-y divide-border">
              {[...tiers].sort((a, b) => a.min_qty - b.min_qty).map((t, i, arr) => {
                const active = qty >= t.min_qty && (i === arr.length - 1 || qty < arr[i + 1].min_qty);
                const range = i === arr.length - 1 ? `${t.min_qty}+` : `${t.min_qty}–${arr[i + 1].min_qty - 1}`;
                return (
                  <div key={t.min_qty} className={`flex items-center justify-between px-4 py-2 text-sm ${active ? "bg-foreground text-background" : ""}`} data-testid={`tier-row-${t.min_qty}`}>
                    <span className="mono">{range} bottles</span>
                    <span className="mono font-bold">{gbp(t.price)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Shipping estimate */}
          <div className="mt-6 border border-border p-4" data-testid="shipping-estimate">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Estimated shipping</div>
              <select data-testid="ship-country-select" value={shipCountry} onChange={(e) => setShipCountry(e.target.value)}
                className="rounded-none border border-border bg-background px-3 py-1.5 text-sm">
                {countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="mt-3 flex items-end justify-between">
              <div className="text-sm text-muted-foreground">
                {ship ? <>To <span className="text-foreground">{shipCountry}</span> · {ship.zoneName}</> : "—"}
              </div>
              <div className="font-heading text-2xl font-black" data-testid="shipping-estimate-value">
                {!ship ? "—" : ship.free ? <span className="text-green-600">FREE</span> : `${gbp(ship.price)}`}
              </div>
            </div>
            {ship && !ship.free && ship.threshold != null && (unit * qty) < ship.threshold && (
              <div className="mono mt-2 text-[11px] text-muted-foreground" data-testid="free-ship-hint">Add {gbp(ship.threshold - unit * qty)} more for FREE {ship.zoneName} shipping</div>
            )}
            <div className="mono mt-2 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">Based on {qty} × {size.label} · final rate confirmed at checkout</div>
          </div>

          {/* Price + add */}
          <div className="mt-8 flex items-center justify-between border-t border-border pt-6">
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Line total</div>
              <div className="font-heading text-3xl font-black" data-testid="line-total">{gbp(unit * qty)}</div>
              <div className="mono text-xs text-muted-foreground">{gbp(unit)} × {qty}</div>
            </div>
            <button data-testid="add-to-cart-btn" onClick={addToCart} className="bg-foreground px-8 py-4 text-sm font-medium text-background transition-transform hover:-translate-y-0.5">
              Add to Cart
            </button>
          </div>
          </div>)}
          {outOfStock && <div className="mt-6"><RestockNotify flavorId={flavor.id} /></div>}
        </div>
      </div>
    </div>
  );
}
